import { useEffect, useState } from "react";
import { X, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const AUTO_CLOSE_MS = 3000;
const NEVER_KEY = (cid) => `vt-discount-never-${cid}`;

// Ventana del descuento vigente: aparece a todos al entrar (una vez por visita) hasta que marquen «No volver a mostrar».
export function DiscountWelcome() {
  const { user, loading } = useAuth();
  const [n, setN] = useState(null);
  const uid = user?.id;
  useEffect(() => {
    if (loading) return;
    api.get("/discount-notice", { offlineFallback: true }).then(({ data }) => {
      if (!data.show || localStorage.getItem(NEVER_KEY(data.campaign_id))) return;
      const key = `vt-discount-notice-${data.campaign_id}-${uid || "guest"}`;
      if (!sessionStorage.getItem(key)) { sessionStorage.setItem(key, "1"); setN(data); }
    }).catch(() => {});
  }, [loading, uid]);
  useEffect(() => {
    if (!n) { return undefined; }
    const t = setTimeout(() => setN(null), AUTO_CLOSE_MS);
    return () => clearTimeout(t);
  }, [n]);
  if (!n) return null;
  const never = () => {
    if (user) api.post("/discount-notice/dismiss", { campaign_id: n.campaign_id }).catch(() => {});
    else localStorage.setItem(NEVER_KEY(n.campaign_id), "1");
    setN(null);
  };
  const ending = n.days_left <= 5;
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-violet-950/50 backdrop-blur-sm p-4 animate-in fade-in" onClick={() => setN(null)} data-testid="discount-notice-overlay">
      <div className="relative w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl ring-2 ring-amber-300 animate-in zoom-in-95" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Descuento para publicar talleres" data-testid="discount-notice">
        <button onClick={() => setN(null)} aria-label="Cerrar" className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/80 text-violet-700 hover:bg-white" data-testid="discount-notice-close"><X className="h-4 w-4" /></button>
        <div className="relative bg-violet-100">
          <img src="/images/discount-badge.jpg" alt="Descuento" className="mx-auto h-44 w-full object-cover" />
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-400 px-4 py-1 text-2xl font-extrabold text-violet-900 shadow" data-testid="discount-notice-pct">{n.pct}% OFF</span>
        </div>
        <div className="space-y-2 bg-gradient-to-b from-white to-amber-50 p-5 text-center">
          <h3 className="text-lg font-extrabold text-violet-800" data-testid="discount-notice-title">{user?.role === "customer" ? `¿Enseñas algo? Publica tus talleres con ${n.pct}% de descuento` : `Publicas talleres con un descuento del ${n.pct}%`}</h3>
          <p className="text-sm text-slate-700">
            {ending
              ? <>¡Queda{n.days_left === 1 ? "" : "n"} {n.days_left === 0 ? "solo hoy" : `${n.days_left} día${n.days_left === 1 ? "" : "s"}`}! Vigente hasta el {formatDate(`${n.until}T12:00:00`)}.</>
              : <>Vigente hasta el {formatDate(`${n.until}T12:00:00`)}.</>}
          </p>
          <p className="flex items-center justify-center gap-1 text-xs font-semibold text-amber-700"><Coins className="h-3.5 w-3.5" />El descuento se aplica al comprar tu pack de Fichas</p>
          <button onClick={never} className="pt-1 text-xs font-medium text-violet-600 underline underline-offset-2 hover:text-violet-800" data-testid="discount-notice-never">No volver a mostrar</button>
        </div>
        <div className="h-1 bg-amber-200"><div className="h-full bg-violet-600 origin-left" style={{ animation: `vtShrink ${AUTO_CLOSE_MS}ms linear forwards` }} /></div>
        <style>{"@keyframes vtShrink{from{transform:scaleX(1)}to{transform:scaleX(0)}}"}</style>
      </div>
    </div>
  );
}
